import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { useThemeMode } from "../../ThemeContext";
import { dataAPI } from "../../helpers/api_service";
import { APP_URLS } from "../../helpers/url_helper";

function CompanyFormList() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTitle, setSearchTitle] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadTemplates = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await dataAPI.getDigitalFormClientForms({
          showLoading: false,
        });
        if (!mounted) return;
        setTemplates(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(error?.message || "Failed to load company forms.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, []);

  const emptyText = useMemo(() => {
    if (loading) return "Loading forms...";
    if (errorMessage) return errorMessage;
    return "No company forms available.";
  }, [loading, errorMessage]);

  const activeTemplates = templates.filter((item) => item?.is_active !== false);
  const filteredTemplates = activeTemplates.filter((item) =>
    String(item?.title || "")
      .toLowerCase()
      .includes(searchTitle.trim().toLowerCase()),
  );

  const safeFileName = (value) =>
    String(value || "company-form")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "company-form";

  const downloadTemplatePdf = (template) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const ensurePageSpace = (required = 24) => {
      if (y + required > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addWrappedText = (text, fontSize = 11, gap = 8) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(String(text || ""), contentWidth);
      const lineHeight = fontSize + 4;
      ensurePageSpace(lines.length * lineHeight + gap);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + gap;
    };

    const drawLine = (lineY, fromX = margin, toX = pageWidth - margin) => {
      ensurePageSpace(20);
      doc.line(fromX, lineY, toX, lineY);
    };

    doc.setFont("helvetica", "bold");
    addWrappedText(template?.title || "Company Form", 18, 2);

    doc.setFont("helvetica", "normal");
    addWrappedText(template?.description || "No description", 11, 20);

    const fields = Array.isArray(template?.schema_json) ? template.schema_json : [];
    fields.forEach((field, index) => {
      const fieldLabel = field?.label || "Untitled Field";
      const fieldType = field?.type || "text";

      doc.setFont("helvetica", "bold");
      addWrappedText(`${index + 1}. ${fieldLabel}`, 12, 8);
      doc.setFont("helvetica", "normal");

      if (fieldType === "radio" || fieldType === "checkbox" || fieldType === "dropdown") {
        const options = Array.isArray(field?.options) ? field.options : [];
        const printableOptions = options.length ? options : ["Option 1", "Option 2"];
        printableOptions.forEach((option) => {
          addWrappedText(`[   ] ${option}`, 10, 4);
        });
        y += 14;
        return;
      }

      if (fieldType === "textarea") {
        ensurePageSpace(110);
        drawLine(y + 14);
        drawLine(y + 36);
        drawLine(y + 58);
        drawLine(y + 80);
        y += 92;
        return;
      }

      ensurePageSpace(44);
      drawLine(y + 14);
      y += 38;
    });

    doc.save(`${safeFileName(template?.title)}.pdf`);
  };

  const shareTemplate = async (template) => {
    const shareTitle = template?.title || "Company Form";
    const shareText = `Please fill this form: ${shareTitle}`;
    const shareUrl = `${window.location.origin}${APP_URLS.COMPANY_FORM_FILL}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText} - ${shareUrl}`);
        alert("Form link copied to clipboard.");
        return;
      }

      alert(`${shareText} - ${shareUrl}`);
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={containerStyle(mode)}>
        <div style={{ marginBottom: 14 }}>
          <input
            type="text"
            value={searchTitle}
            onChange={(event) => setSearchTitle(event.target.value)}
            placeholder="Search by form title..."
            style={searchInputStyle(mode)}
          />
        </div>

        {!filteredTemplates.length ? (
          <div style={emptyCardStyle(mode)}>{emptyText}</div>
        ) : (
          <div style={tableWrapperStyle(mode)}>
            <table style={tableStyle()}>
              <thead>
                <tr>
                  <th style={headCellStyle(mode)}>Title</th>
                  <th style={headCellStyle(mode)}>Description</th>
                  <th style={headCellStyle(mode)}>Fields</th>
                  <th style={headCellStyle(mode)}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id} style={rowStyle(mode)}>
                    <td style={bodyCellStyle(mode)}>
                      {template?.title || "Untitled Form"}
                    </td>
                    <td style={bodyCellStyle(mode)}>
                      {template?.description || "No description"}
                    </td>
                    <td style={bodyCellStyle(mode)}>
                      {Array.isArray(template?.schema_json)
                        ? template.schema_json.length
                        : 0}
                    </td>
                    <td style={bodyCellStyle(mode)}>
                      <div style={actionWrapStyle()}>
                        <button
                          type="button"
                          style={openBtnStyle()}
                          onClick={() =>
                            navigate(APP_URLS.COMPANY_FORM_FILL, {
                              state: { template },
                            })
                          }
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          style={downloadBtnStyle()}
                          onClick={() => downloadTemplatePdf(template)}
                        >
                          Download PDF
                        </button>
                        <button
                          type="button"
                          style={shareBtnStyle()}
                          onClick={() => shareTemplate(template)}
                        >
                          Share
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const containerStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  maxWidth: 980,
});

const searchInputStyle = (mode) => ({
  width: "100%",
  maxWidth: 360,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const emptyCardStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const tableWrapperStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  borderRadius: 10,
  overflowX: "auto",
});

const tableStyle = () => ({
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 680,
});

const headCellStyle = (mode) => ({
  textAlign: "left",
  padding: "11px 12px",
  borderBottom: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  background: mode === "dark" ? "#171717" : "#f8fafc",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
  fontWeight: 600,
});

const rowStyle = (mode) => ({
  borderBottom: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
});

const bodyCellStyle = (mode) => ({
  padding: "11px 12px",
  color: mode === "dark" ? "#d0d5dd" : "#0f172a",
  fontSize: 13,
});

const openBtnStyle = () => ({
  border: "none",
  background: "#009391",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const downloadBtnStyle = () => ({
  border: "none",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const shareBtnStyle = () => ({
  border: "none",
  background: "#7c3aed",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const actionWrapStyle = () => ({
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
});

export default CompanyFormList;
